import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Bot, X, Send, Sparkles, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChatbotHistory } from '../../hooks/useChatbotHistory';

// Giả lập user_id hiện tại
const USER_ID = "199224";

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  const { 
    messages, 
    loadInitialHistory, 
    loadMoreHistory, 
    loading, 
    loadingMore, 
    hasMore,
    isFirstLoadComplete,
    sendMessageStream
  } = useChatbotHistory(USER_ID);

  const scrollRef = useRef(null);
  const [prevScrollHeight, setPrevScrollHeight] = useState(0);
  const [isPrepending, setIsPrepending] = useState(false);
  const hasAutoScrolledToBottom = useRef(false);

  // Mở Chatbox lần đầu thì fetch lịch sử
  const toggleChat = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    
    if (willOpen && !isFirstLoadComplete) {
      loadInitialHistory();
    }
  };

  // Cuộn xuống dưới cùng
  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  // Tự động kéo xuống đáy khi vừa load màn hình xong
  useEffect(() => {
    if (isFirstLoadComplete && messages.length > 0 && !hasAutoScrolledToBottom.current) {
      scrollToBottom();
      hasAutoScrolledToBottom.current = true;
    }
  }, [isFirstLoadComplete, messages]);

  // Tự động kéo xuống đáy liên tục nếu đang có tin nhắn isStreaming
  const isCurrentlyStreaming = messages.length > 0 && messages[messages.length - 1].isStreaming;
  useEffect(() => {
    if (isCurrentlyStreaming) {
      scrollToBottom(); // Trượt liên tục xuống dưới khi nhận ký tự mới
    }
  }, [messages, isCurrentlyStreaming]);

  // Reverse Infinite Scroll
  const handleScroll = (e) => {
    const container = scrollRef.current;
    if (!container) return;

    if (container.scrollTop < 10 && hasMore && !loadingMore && isFirstLoadComplete) {
      setPrevScrollHeight(container.scrollHeight);
      setIsPrepending(true);
      loadMoreHistory();
    }
  };

  // Bù trừ vị trí cuộn cho UX Load More mượt mà
  useLayoutEffect(() => {
    if (isPrepending && scrollRef.current && !loadingMore) {
      const container = scrollRef.current;
      container.scrollTop = container.scrollHeight - prevScrollHeight;
      setIsPrepending(false);
    }
  }, [messages, loadingMore, isPrepending, prevScrollHeight]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const question = inputValue;
    setInputValue(''); // Reset field input nhanh chóng
    
    // Auto trượt xuống sớm để user thấy câu hỏi của mình
    setTimeout(() => scrollToBottom(), 50);

    // Kích hoạt hàm streaming, truyền chuỗi câu hỏi
    await sendMessageStream(question);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="mb-4 w-80 sm:w-96 h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-100"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 flex justify-between items-center text-white">
              <div className="flex items-center space-x-2">
                <div className="bg-white/20 p-1.5 rounded-full flex items-center justify-center">
                  <Bot size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Trợ lý AI</h3>
                  <div className="flex items-center text-xs text-blue-100">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 mr-1.5"></span>
                    Đang hoạt động
                  </div>
                </div>
              </div>
              <button 
                onClick={toggleChat}
                className="text-white hover:bg-white/10 p-1.5 rounded-full transition-colors"
                aria-label="Đóng Chat"
              >
                <X size={20} />
              </button>
            </div>

            {/* Chat Area */}
            <div 
               ref={scrollRef}
               onScroll={handleScroll}
               className="flex-1 p-4 overflow-y-auto bg-slate-50 flex flex-col space-y-4"
            >
              {loading && !isFirstLoadComplete && (
                <div className="flex-1 flex flex-col items-center justify-center space-y-3">
                  <Loader2 size={32} className="text-blue-500 animate-spin" />
                  <p className="text-sm text-gray-500">Đang khởi tạo trò chuyện...</p>
                </div>
              )}

              {loadingMore && (
                <div className="flex justify-center py-2">
                  <Loader2 size={18} className="text-gray-400 animate-spin" />
                </div>
              )}

              {isFirstLoadComplete && messages.map((msg) => (
                <div 
                  key={msg.id} 
                  className={`flex ${msg.isBot ? 'justify-start' : 'justify-end'}`}
                >
                  <div 
                    className={`max-w-[85%] p-3 rounded-2xl text-sm whitespace-pre-wrap ${
                      msg.isBot 
                        ? msg.isError 
                            ? 'bg-red-50 text-red-600 border border-red-200'
                            : 'bg-white text-gray-800 border border-gray-100 shadow-sm rounded-tl-sm' 
                        : 'bg-blue-600 text-white rounded-tr-sm shadow-sm'
                    }`}
                  >
                    {msg.text}
                    {/* Hiệu ứng gõ chữ (Ba chấm) */}
                    {msg.isStreaming && (
                      <span className="inline-flex space-x-1 items-center ml-1 h-3 align-middle">
                        <motion.span
                          animate={{ y: [0, -4, 0] }}
                          transition={{ repeat: Infinity, duration: 0.6, delay: 0 }}
                          className="w-1.5 h-1.5 bg-gray-400 rounded-full inline-block"
                        />
                        <motion.span
                          animate={{ y: [0, -4, 0] }}
                          transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }}
                          className="w-1.5 h-1.5 bg-gray-400 rounded-full inline-block"
                        />
                        <motion.span
                          animate={{ y: [0, -4, 0] }}
                          transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }}
                          className="w-1.5 h-1.5 bg-gray-400 rounded-full inline-block"
                        />
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-100">
              <form 
                onSubmit={handleSendMessage}
                className="flex items-center space-x-2 bg-slate-50 border border-gray-200 rounded-full px-4 py-2 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all"
              >
                <input
                  type="text"
                  value={inputValue}
                  disabled={isCurrentlyStreaming}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={isCurrentlyStreaming ? "AI đang trả lời..." : "Hỏi AI bất kỳ điều gì..."}
                  className="flex-1 bg-transparent border-none outline-none text-sm text-gray-700 placeholder-gray-400 disabled:opacity-50"
                />
                <button 
                  type="submit"
                  disabled={!inputValue.trim() || isCurrentlyStreaming}
                  className={`p-1.5 rounded-full flex items-center justify-center transition-colors ${
                    inputValue.trim() && !isCurrentlyStreaming
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                  aria-label="Gửi tin nhắn"
                >
                  <Send size={16} />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleChat}
        className="w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full shadow-lg flex items-center justify-center text-white relative group focus:outline-none focus:ring-4 focus:ring-blue-500/30 transition-shadow"
        aria-label="Mở Trợ lý AI"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: 90 }}
              transition={{ duration: 0.15 }}
            >
              <X size={26} />
            </motion.div>
          ) : (
            <motion.div
              key="bot"
              initial={{ opacity: 0, rotate: 90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: -90 }}
              transition={{ duration: 0.15 }}
              className="relative"
            >
              <Bot size={28} />
              <motion.div 
                className="absolute -top-1 -right-1"
                animate={{ rotate: 360 }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles size={12} className="text-yellow-300" />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
};

export default Chatbot;
